// Phase 3B (Task 3.4) — <CopilotFeed /> chat thread.
//
// The single rendering surface for a boat's Copilot Session. It consumes
// useCopilotSession(tenantId, boatId) — the unified per-boat reducer that
// multiplexes the LIVE per-file status stream + the LOGGED narrative timeline
// off one channel + replay — and renders it as a chatbot-style thread of
// `assistant` bubbles, adapted from the Metronic Todo "AI Assistant" aside.
//
// Composition (top → bottom):
//   1. The chat thread (ScrollArea): one `assistant` bubble per logged feed
//      item (finding/step/insight/job) PLUS one live spinner bubble per
//      in-progress per-file `row` (reading/classifying/queued). A failed row
//      renders the ✗ bubble.
//   2. The terminal block: when the run reaches a terminal (isComplete /
//      isDoneWithErrors / isFailed) the headline + a "Review your listing" CTA
//      render (both MOVED here from the retired ExtractionStatusPanel).
//   3. CopilotChatInput — the disabled "coming soon" composer.
//
// Per-bubble affordance (§3B resolved design):
//   - in-progress `row` (reading/etc, not yet terminal) → spinner (Loader2);
//   - failed `row` → ✗ AlertCircle (destructive);
//   - conflict finding (type:'finding' AND payload.detail.reason==='conflict')
//     → ⚠ AlertCircle (amber);
//   - read finding (type:'finding', else) → ✓ Check;
//   - step/insight/job → an Info default icon.
// The terminal monotonically flips reading rows to done in the hook, so once
// terminal.isComplete is true NO spinner remains (the §6.5 stuck-spinner
// guardrail) — the rendering simply keys off the row state the hook reports.
//
// Accessibility parity with the old role="log": the thread is an aria-live
// "polite" region with a descriptive aria-label so AT announces new bubbles.
//
// NOTE: prose-* classes are intentionally NOT used (the @tailwindcss/typography
// plugin is not installed — they'd be inert). Content renders as plain text.

import type { ReactNode } from 'react';
import { Loader2, Check, AlertCircle, Info, Bot, CornerUpRight } from 'lucide-react';
import { Button } from '@marine/ui/button';
import { ScrollArea } from '@marine/ui/scroll-area';
import { Avatar, AvatarFallback } from '@marine/ui/avatar';
import type { FeedItem, ProgressLine, StatusRow } from './hooks/useCopilotSession';
import { useSharedCopilotSession } from './CopilotSessionContext';
import { CopilotChatInput } from './CopilotChatInput';

interface CopilotFeedProps {
  tenantId: string | null;
  boatId: string | null;
  /** Jump to a form field/section (reserved for a future per-bubble affordance). */
  onJumpToField?: (field: string) => void;
  /** Fires when the dealer clicks the terminal "Review your listing" CTA. */
  onReview?: () => void;
  /**
   * Render the in-feed "AI Deckhand" heading. Desktop suppresses it (the title
   * moves to the sidebar's slim header bar beside the collapse control); the
   * mobile bottom-sheet keeps it as the visible title. Defaults to true.
   */
  showHeading?: boolean;
}

/** True when a logged finding describes a merge conflict (vs a routine read). */
function isConflictFinding(item: FeedItem): boolean {
  if (item.type !== 'finding') return false;
  const detail = item.payload?.detail as Record<string, unknown> | undefined;
  return Boolean(detail && detail.reason === 'conflict');
}

/* -------------------------------------------------------------------------- */
/* Bubble shell — assistant avatar + a rounded message bubble.                */
/* -------------------------------------------------------------------------- */

function AssistantBubble({
  icon,
  children,
  testId,
}: {
  icon: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex gap-3" data-testid={testId}>
      <Avatar className="size-8 shrink-0">
        <AvatarFallback className="border-0 bg-gradient-to-br from-zinc-900 to-zinc-500 text-xs text-white">
          <Bot className="size-4" />
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 max-w-[85%] flex-col items-start gap-1">
        <div className="flex items-start gap-2 rounded-2xl rounded-tl-md bg-muted/60 px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
          <span className="mt-0.5 shrink-0">{icon}</span>
          <span className="min-w-0">{children}</span>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Per-file live row bubble (spinner / done / failed).                        */
/* -------------------------------------------------------------------------- */

function RowBubble({
  row,
  isTerminal,
}: {
  row: StatusRow;
  isTerminal: boolean;
}) {
  const label = row.label ?? row.name ?? `File ${row.mediaId}`;

  // The hook already projects reading rows to `done` at terminal, but guard here
  // too: NEVER render the spinner when the session is terminal, even if a caller
  // passes a stale `reading` row. No spinner can outlive a finished run.
  const inProgress =
    row.state === 'queued' ||
    row.state === 'classifying' ||
    row.state === 'reading';
  const spinning = inProgress && !isTerminal;

  let icon: ReactNode;
  if (row.state === 'failed') {
    icon = (
      <AlertCircle
        className="h-4 w-4 text-destructive"
        role="img"
        aria-label="Failed"
      />
    );
  } else if (spinning) {
    icon = (
      <Loader2
        className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400"
        role="img"
        aria-label="Reading"
      />
    );
  } else {
    icon = (
      <Check
        className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
        role="img"
        aria-label="Done"
      />
    );
  }

  return (
    <AssistantBubble icon={icon} testId="copilot-row">
      <span className="block min-w-0 truncate" title={row.name ?? label}>
        {label}
      </span>
      {row.state === 'failed' && row.userMessage && (
        // Per-item failure surfaces only the user-safe message (never internals).
        <span className="mt-1 block text-[0.8125rem] font-normal text-destructive">
          {row.userMessage}
        </span>
      )}
    </AssistantBubble>
  );
}

/* -------------------------------------------------------------------------- */
/* Logged narrative bubble (finding / step / insight / job).                  */
/* -------------------------------------------------------------------------- */

function FeedBubble({
  item,
  onJumpToField,
}: {
  item: FeedItem;
  onJumpToField?: (field: string) => void;
}) {
  let icon: ReactNode;
  if (isConflictFinding(item)) {
    icon = (
      <AlertCircle
        className="h-4 w-4 text-amber-600 dark:text-amber-400"
        role="img"
        aria-label="Conflict"
        data-testid="copilot-conflict-icon"
      />
    );
  } else if (item.type === 'finding') {
    icon = (
      <Check
        className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
        role="img"
        aria-label="Found"
        data-testid="copilot-finding-icon"
      />
    );
  } else {
    icon = (
      <Info
        className="h-4 w-4 text-muted-foreground"
        role="img"
        aria-label={item.type}
      />
    );
  }

  // A finding may carry a jump-to-field affordance via payload.detail.field.
  const detail = item.payload?.detail as Record<string, unknown> | undefined;
  const fieldRef =
    typeof detail?.field === 'string' ? (detail.field as string) : undefined;

  return (
    <AssistantBubble icon={icon} testId="copilot-feed-item">
      <span className="block">{item.content}</span>
      {fieldRef && onJumpToField && (
        <button
          type="button"
          onClick={() => onJumpToField(fieldRef)}
          className="mt-1 inline-flex items-center gap-1 text-[0.8125rem] font-normal text-muted-foreground hover:text-foreground"
          aria-label={`Jump to ${fieldRef} field`}
        >
          <CornerUpRight className="h-3.5 w-3.5" role="img" aria-hidden="true" />
          Jump to field
        </button>
      )}
    </AssistantBubble>
  );
}

/* -------------------------------------------------------------------------- */
/* #1178 — live lines (progress, insights): ephemeral, not in the logged feed. */
/* -------------------------------------------------------------------------- */

function LiveLinesBubble({ lines, icon, testId }: { lines: ProgressLine[]; icon: ReactNode; testId: string }) {
  return (
    <AssistantBubble icon={icon} testId={testId}>
      {lines.map((line) => (
        <span key={line.key} className="block">
          {line.text}
        </span>
      ))}
    </AssistantBubble>
  );
}

/* -------------------------------------------------------------------------- */
/* Feed                                                                       */
/* -------------------------------------------------------------------------- */

export function CopilotFeed({
  tenantId,
  boatId,
  onJumpToField,
  onReview,
  showHeading = true,
}: CopilotFeedProps) {
  const { feed, rows, terminal, failedCount, progress, insights, phaseDone } =
    useSharedCopilotSession(tenantId, boatId);

  const isTerminal =
    terminal.isComplete || terminal.isDoneWithErrors || terminal.isFailed;
  const isEmpty =
    feed.length === 0 && rows.length === 0 && !isTerminal
    && progress.length === 0 && insights.length === 0 && phaseDone === null;

  return (
    <div className="flex h-full flex-col p-4" data-testid="copilot-feed">
      {showHeading && (
        <h3 className="shrink-0 pb-3 text-base font-semibold">AI Deckhand</h3>
      )}

      <ScrollArea className="min-h-0 flex-1">
        {/* The chat thread — an ARIA log landmark (parity with the old
            role="log"): a polite live region announcing each new bubble. */}
        <div
          role="log"
          aria-live="polite"
          aria-label="AI activity timeline"
          className="space-y-4 pr-2"
        >
          {isEmpty && (
            <div className="space-y-2">
              <p className="text-sm font-normal text-muted-foreground">
                AI activity will appear here
              </p>
              <p className="text-sm font-normal text-muted-foreground">
                As AI agents read your documents, fill specs, and generate your
                listing, you&apos;ll see a live feed of what&apos;s happening.
              </p>
            </div>
          )}

          {/* Logged narrative bubbles (seq-ordered by the hook). */}
          {feed.map((item) => (
            <FeedBubble key={item.seq} item={item} onJumpToField={onJumpToField} />
          ))}

          {/* Live per-file rows. Once a terminal arrives the hook reports each
              row as done/failed, so no spinner survives the terminal. */}
          {rows.map((row) => (
            <RowBubble
              key={String(row.mediaId)}
              row={row}
              isTerminal={isTerminal}
            />
          ))}

          {/* #1178 — what the read is finding right now (document values only). */}
          {!isTerminal && progress.length > 0 && (
            <LiveLinesBubble
              lines={progress}
              testId="copilot-live-progress"
              icon={<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" role="img" aria-label="Reading" />}
            />
          )}

          {!isTerminal && phaseDone === 'documents' && (
            <AssistantBubble
              testId="copilot-phase-done"
              icon={<Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" role="img" aria-label="Done" />}
            >
              Documents read. Checking the photos against them…
            </AssistantBubble>
          )}

          {insights.length > 0 && (
            <LiveLinesBubble
              lines={insights}
              testId="copilot-live-insights"
              icon={<Info className="h-4 w-4 text-muted-foreground" role="img" aria-label="Insight" />}
            />
          )}

          {/* Terminal block — headline + Review CTA (moved from the retired
              ExtractionStatusPanel). Renders on any terminal state. */}
          {isTerminal && (
            <div className="space-y-2" data-testid="copilot-terminal">
              {terminal.headline && (
                <p className="text-sm font-semibold text-foreground">
                  {terminal.headline}
                </p>
              )}
              {(terminal.isDoneWithErrors || terminal.isFailed) &&
                failedCount > 0 && (
                  <p
                    className="text-[0.8125rem] font-normal text-destructive"
                    data-testid="copilot-failed-note"
                  >
                    {failedCount === 1
                      ? `${failedCount} file couldn’t be read`
                      : `${failedCount} files couldn’t be read`}
                  </p>
                )}
              {(terminal.isComplete || terminal.isDoneWithErrors) && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="font-normal"
                  onClick={onReview}
                  data-testid="copilot-review-cta"
                >
                  Review your listing
                </Button>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Disabled composer ("coming soon"). */}
      <div className="shrink-0 pt-4">
        <CopilotChatInput />
      </div>
    </div>
  );
}
